/* eslint-disable @typescript-eslint/no-non-null-assertion */
import type { MutableRefObject } from 'react';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useI18n } from '@whizz/react-i18n';
import type { ScaleLinear, Selection } from 'd3';
import * as d3 from 'd3';
import { usePrevious, useWindowSize } from 'react-use';
import { createGlobalStyle } from 'styled-components';
import type { ChartDataType, ChartIconType, DrawProps, ReportPageScatterChartQueueType } from '../types';
import type { TopicChartType } from '../../../mapper/groupTopicReportMapper';
import type { HighlightedProps } from '../../../pages/types';
import d3Helpers from '../helpers/func';
import Loader from '../../loader/Loader';
import { ButtonIcon } from '../../../components';
import { getIconComponent } from '../../../util/reactFunc';
import { GLOBAL_ICONS } from '../../../util/vars';

import {
  STUDENT_PROGRESS_MIN_HEIGHT,
  TOPIC_PROGRESS_BAR_HEIGHT,
  TOPIC_PROGRESS_CHARTS_ANIMATION_DURATION,
  TOPIC_PROGRESS_CIRCLE_RADIUS,
  TOPIC_PROGRESS_CLASSES,
  TOPIC_PROGRESS_COLORS,
  TOPIC_PROGRESS_FILTERED_ARROW_STROKE,
  TOPIC_PROGRESS_GRADIENT_FILTERED_ID,
  TOPIC_PROGRESS_GRADIENT_FILTERED_ID_REVERSE,
  TOPIC_PROGRESS_GRADIENT_ID,
  TOPIC_PROGRESS_GRADIENT_ID_REVERSE,
  TOPIC_PROGRESS_LABELS_WIDTH_PERCENT,
  TOPIC_PROGRESS_LABEL_SIZE,
  TOPIC_PROGRESS_STROKE_WIDTH,
  TOPIC_PROGRESS_TITLE_OFFSET,
} from '../helpers/vars';
import type { IconsType } from '../../../context/IconContext';
import type { AllIconType } from '../../../util/types';
import { IconContext } from '../../../context/IconContext';
import { PdfContext } from '../../../context/PdfContext';
import Alert from '../../alert/Alert';
import StudentProgress from '../student-progress/StudentProgress';
import { DateTime } from 'luxon';

type ScatterChartProps = {
  elements: {
    id: number,
    fullName: string,
    gender?: number | null,
    color?: string | null,
    value: number,
    progress: number,
    isHighlighted: boolean,
    x: number,
    y: number,
  }[];
  height: number
};

interface ChartDataProps {
  topic: ChartDataTopicProps;
  start: number;
  end: number;
  filtered: {
    start: number;
    end: number;
  } | null;
  children: ScatterChartProps;
}

interface ChartDataTopicProps {
  index: number;
  name: string;
}

interface Props extends Pick<HighlightedProps, 'highlightedIds'> {
  topics: TopicChartType[];
  showOthersData: boolean;
  showSensitiveData?: boolean;
  type?: ChartIconType;
  play?: boolean;
}

interface ScatterPosition {
  width: number;
  trigger: DOMRect;
}

interface ScatterLineChartProps {
  children: JSX.Element;
  visible: boolean;
  onClose: () => void;
  icon?:  AllIconType;
  title?: string;
  position?: ScatterPosition;
}

const GlobalStyle = createGlobalStyle`
.${TOPIC_PROGRESS_CLASSES.grid} {
  path {
    opacity: 0;
    visibility: hidden;
  }
  line {
    stroke: ${TOPIC_PROGRESS_COLORS.bar_border};
    stroke-dasharray: 1.5;
    opacity: 0.25;
  }
}
.${TOPIC_PROGRESS_CLASSES.label} {
  font-weight: bold;
  font-style: italic;  
}
.${TOPIC_PROGRESS_CLASSES.chevron}:not(.disabled) {
  cursor: pointer;
}
.${TOPIC_PROGRESS_CLASSES.chevron}.disabled {
  opacity: 0.25;
}
`;

const truncateToOneDecimal = (num: number) => Math.trunc(num * 10) / 10;

const roundDown = (num: number) =>
  (num - Math.floor(num)) === 0.5 ? num :
    ((num - Math.floor(num)) > 0.5 ? num - ((num - Math.floor(num)) - 0.5) : Math.floor(num));

const roundUp = (num: number) =>
  (Math.ceil(num) - num) === 0.5 ? num :
    ((Math.ceil(num) - num) > 0.5 ? (Math.ceil(num) - num) - 0.5 + num : Math.ceil(num));

const roundStart = (start: number) => {
  const startTruncated = truncateToOneDecimal(start);
  const startRoundedTwoDecimals = roundToTwoDecimals(startTruncated);
  return roundDown(startRoundedTwoDecimals);
};

const roundEnd = (end: number) => {
  const endUpdated = increaseSecondDecimalIfNonZero(end);
  const endTruncated = truncateToOneDecimal(endUpdated);
  const endRoundedTwoDecimals = roundToTwoDecimals(endTruncated);
  return roundUp(endRoundedTwoDecimals);
};

const roundToTwoDecimals = (num: number) => Math.round((num + Number.EPSILON) * 100) / 100;

const increaseSecondDecimalIfNonZero = (num: number) => {
  const roundedNum = roundToTwoDecimals(num);
  const initialUpdatedNumber = truncateToOneDecimal(roundedNum * 10);
  const flooredUpdatedNumber = Math.trunc(truncateToOneDecimal(roundedNum * 10));
  const distanceToNextDecimal = roundToTwoDecimals((1 - (initialUpdatedNumber - flooredUpdatedNumber)) / 10);

  return initialUpdatedNumber === flooredUpdatedNumber ? roundedNum : roundedNum + distanceToNextDecimal;
};

const removeNullValues = (values: Array<number | null>) => (
  values.filter(Boolean) as number[]
);

// We want at least 1 of separation between start and end. If that does not happen, we force it
const adjustStartEndValues = (start: number | null, end: number | null) => {
  const minSeparation = 1;
  if (null === end && null !== start) return [ start, start + minSeparation ];
  if (null !== end && null === start) return [ end, end + minSeparation ];
  if (null === end || null === start) return [ 0, minSeparation ];
  if (Math.max(start, end) - Math.min(start, end) < minSeparation) return [ Math.min(start, end), Math.min(start, end) + minSeparation ];
  return [ start, end ];
};

const calculateAverage = (values: number[]): number | null =>
  values.reduce((a, b) => a + b, 0) / (values.length - 1) || null;

const getRawStartEndClassValues = (topics: TopicChartType[]): [number | null, number | null] => (
  topics.reduce((acc, topic) => {
    let [ startAcc, endAcc ] = acc;

    const roundedStartAvgTopicAge = roundStart(topic.startAvgTopicAge);
    const roundedEndAvgTopicAge = roundEnd(topic.endAvgTopicAge);

    startAcc = Math.min(...removeNullValues([ startAcc, roundedStartAvgTopicAge, roundedEndAvgTopicAge ]));
    endAcc = Math.max(...removeNullValues([ endAcc, roundedStartAvgTopicAge, roundedEndAvgTopicAge ]));

    return [ startAcc, endAcc ];
  }, [ null, null ] as [number | null, number | null])
);

const getRawStartEndStudentsValues = (topics: TopicChartType[], highlightedIds: number[]): [number | null, number | null] => (
  topics.reduce((acc, topic) => {
    let [ startAcc, endAcc ] = acc;

    const [ elementsStartTopicAge, elementsEndTopicAge ] = topic.elements.reduce((acc2, element) => {
      const [ startAcc2, endAcc2 ] = acc2;
      const isStudentSelected = highlightedIds.includes(element.id);

      if (!isStudentSelected) return acc2;

      return [ [ ...startAcc2, element.startTopicAge ], [ ...endAcc2, element.endTopicAge ] ];
    }, [ [ 0 ], [ 0 ] ]);

    const hasTopicAge = [ 0 ] !== elementsStartTopicAge && [ 0 ] !== elementsEndTopicAge;
    if (!hasTopicAge) return acc;

    const elementsAvgStartTopicAge = calculateAverage(elementsStartTopicAge);
    const elementsAvgStartTopicAgeRounded = roundStart(elementsAvgStartTopicAge || 0);

    const elementsAvgEndTopicAge = calculateAverage(elementsEndTopicAge);
    const elementsAvgEndTopicAgeRounded = roundEnd(elementsAvgEndTopicAge || 0);

    const hasTopicAgeAvg = (null !== elementsAvgStartTopicAge && null !== elementsAvgEndTopicAge);
    const hasPreviousValues = null !== startAcc && null !== endAcc;

    // We need to check for previous values, otherwise Math.min or Math.max of nothing would be Infinity or -Infinity
    if (!hasPreviousValues || hasTopicAgeAvg) {
      startAcc = Math.min(...removeNullValues([ startAcc, elementsAvgStartTopicAgeRounded, elementsAvgEndTopicAgeRounded ]));
      endAcc = Math.max(...removeNullValues([ endAcc, elementsAvgStartTopicAgeRounded, elementsAvgEndTopicAgeRounded ]));
    }

    return [ startAcc, endAcc ];
  }, [ null, null ] as [number | null, number | null])
);

const TopicProgress = ({ topics, type = 'student', showOthersData, highlightedIds, showSensitiveData = true, play = true }: Props): JSX.Element => {
  const prevshowOthersData = usePrevious(showOthersData);
  const prevHighlightedIds = usePrevious(highlightedIds);
  const prevPrevHighlightedIds = usePrevious(prevHighlightedIds);
  const { translate } = useI18n();
  const [ showChildren, setShowChildren ] = useState<number[]>([]);
  const prevShowChildren = usePrevious(showChildren);
  const { width: windowSize } = useWindowSize();
  const containerRef = useRef() as MutableRefObject<HTMLDivElement>;
  const [ width, setWidth ] = useState(0);
  const [ scatterPosition, setScatterPosition ] = useState<ScatterPosition>();

  const icons = useContext(IconContext);
  const { isPdf, pdfIds, setIsPdfReady } = useContext(PdfContext);

  const getMinMaxAxisValues = (): number[] => {
    const [ startClass, endClass ] = getRawStartEndClassValues(topics);

    const studentsSelected = highlightedIds.length > 0;
    if (!studentsSelected) return adjustStartEndValues(startClass, endClass);

    const [ startStudents, endStudents ] = getRawStartEndStudentsValues(topics, highlightedIds);

    if (!showOthersData) return adjustStartEndValues(startStudents, endStudents);

    const start = Math.min(...removeNullValues([ startClass, startStudents ]));
    const end = Math.max(...removeNullValues([ endClass, endStudents ]));
    return adjustStartEndValues(start, end);
  };

  const prepareData = (type: ChartIconType, filterByIds: number[] | null, xScale: ScaleLinear<number, number>): ChartDataProps[] => {
    return topics.reduce(
      (acc, topic) => {
        const chartData: ChartDataProps = {
          topic: {
            name: topic.name,
            index: topic.index,
          },
          start: topic.startAvgTopicAge,
          end: topic.endAvgTopicAge,
          filtered: null,
          children: prepareChildrenData(topic, type, filterByIds, xScale),
        };

        if (null != filterByIds && 0 < filterByIds.length) {
          const filteredItems = topic.elements
            .filter((element) => filterByIds.includes(element.id) && null != element.startTopicAge);

          if (0 === filteredItems.length) {
            if (!showOthersData) {
              return acc;
            }

            return [
              ...acc,
              chartData,
            ];
          }

          chartData.filtered = {
            start: filteredItems.map((element) => element.startTopicAge!).reduce((a, b) => a + b, 0) / filteredItems.length,
            end: filteredItems.map((element) => element.endTopicAge!).reduce((a, b) => a + b, 0) / filteredItems.length,
          };
        }

        return [
          ...acc,
          chartData,
        ];
      },
      [] as ChartDataProps[],
    );
  };

  const prepareChildrenData = (topic: TopicChartType, type: ChartIconType, filterByIds: number[] | null, xScale: ScaleLinear<number, number>): ScatterChartProps => {
    const element_size = d3Helpers.getElementSize(type);

    const elements = topic.elements
      .map((element) => ({
        id: element.id,
        fullName: element.fullName,
        gender: type === 'student' ? element.gender : null,
        color: type !== 'student' ? element.color : null,
        progress: element.progressTopicAge,
        value: element.endTopicAge,
        isHighlighted: null != filterByIds && filterByIds.includes(element.id),
      }));

    const queue = d3Helpers.queue(
      elements.map((element, index) => {
        return {
          x: xScale(element.value),
          y: null,
          id: element.id,
          index: index,
          isHighlighted: element.isHighlighted,
          title: element.fullName,
          value: element.value,
        } as ReportPageScatterChartQueueType;
      }),
      element_size,
      type === 'year',
    );

    elements.sort((a, b) => {
      return d3.ascending(a.isHighlighted, b.isHighlighted) || d3.ascending(a.value, b.value);
    });

    return {
      elements: elements.map((element) => {
        const onQueue = queue.find((qElement) => qElement.id === element.id);
        return {
          ...element,
          x: onQueue?.x ?? xScale(element.value),
          y: (onQueue?.y ?? 0) + (STUDENT_PROGRESS_MIN_HEIGHT / 2),
        };
      }),
      height: d3
        .max(queue.map((element) => element.y as number)) as number + (element_size.highlighted_height * 1.25) + STUDENT_PROGRESS_MIN_HEIGHT,
    };
  };

  const draw = (
    topics: TopicChartType[],
    {
      width,
      margin,
      padding,
    }: DrawProps,
    disabledMainAnimations = false,
    disabledHighlightedAnimations = false,
  ): void => {
    // Our condition makes draw not to trigger with null icons (not loaded)
    const fetchedIcons = (icons as IconsType);

    // Label container width:
    const labelWidth = (TOPIC_PROGRESS_LABELS_WIDTH_PERCENT * width) / 100;
    const labelOffset = showSensitiveData ? 75 : 20;

    // Grid container width:
    const axisWidthOffset = 50;
    const axisWidth = width - labelWidth - axisWidthOffset;

    // Gets x-axis values:
    const [ xMin, xMax ] = getMinMaxAxisValues();

    // Checks x-min:
    // if (xMin < TOPIC_PROGRESS_FIXED_MIN) {
    //   xMin = TOPIC_PROGRESS_FIXED_MIN;
    // }

    // Set up x-scale:
    const xScale = d3
      .scaleLinear<number>()
      .domain([ xMin, xMax ])
      .range([ labelWidth, width - axisWidthOffset ]);

    // Prepares data
    const formattedData = prepareData(type, highlightedIds, xScale);

    // Prepares container:
    const container = d3Helpers.prepareContainer(containerRef);

    // Prepares tooltip:
    const tooltip = d3Helpers.prepareTooltip(container);

    // Gets actual bar height:
    const computedBarHeight = TOPIC_PROGRESS_BAR_HEIGHT - (padding ?? 0);
    // Gets bar y-position:
    const barYPosition = (index: number): number => (showSensitiveData ? TOPIC_PROGRESS_TITLE_OFFSET : 0) + TOPIC_PROGRESS_BAR_HEIGHT * (index + 1) + (padding ? padding / 2 : 0);

    // Computes svg height:
    const height = formattedData.length * TOPIC_PROGRESS_BAR_HEIGHT + barYPosition(0);

    // Main svg:
    const svg = d3
      .create('svg')
      .attr('width', width + margin.left + margin.right)
      .attr('height', height)
      .attr('viewBox', `0 0 ${width} ${height}`)
      .attr('style', 'max-width: 100%; height: auto;');

    // xAxis group:
    if (showSensitiveData) {
      // Set up x-axis:
      const xAxis = d3
        .axisTop(xScale)
        .ticks(3)
        .tickFormat((x) => (x as number).toFixed(1));

      svg
        .append('g')
        .attr('transform', `translate(${0},${TOPIC_PROGRESS_TITLE_OFFSET + (TOPIC_PROGRESS_BAR_HEIGHT / 1.5)})`)
        .call(xAxis)
        .attr('font-size', TOPIC_PROGRESS_LABEL_SIZE)
        .call((g) =>
          g
            .append('text')
            .attr('y', margin.bottom - 4)
            .attr('fill', 'currentColor')
            .attr('text-anchor', 'end'),
        )
        .append('text')
        .attr('x', ((width - 26) / 3) * 2)
        .attr('y', -TOPIC_PROGRESS_TITLE_OFFSET)
        .attr('text-anchor', 'middle')
        .attr('fill', 'currentColor')
        .text(translate('pages.REPORTS.SECTIONS.TOPIC_PROGRESS_TABLE.title').toString())
      ;

      svg
        .append('g')
        // (26) is based on ChartIcon width!! Do NOT change, please.
        .attr('transform', `translate(${labelWidth - labelOffset + 26}, ${TOPIC_PROGRESS_TITLE_OFFSET})`)
        .call((group) => {
          group
            .node()
            ?.appendChild(fetchedIcons.chartIcon.cloneNode(true));
        })
      ;
    }

    // Row container:
    const row = svg
      .selectAll(`.${TOPIC_PROGRESS_CLASSES.row}`)
      .data(formattedData)
    ;

    // Prepares data:
    const rowData = row
      .enter()
      .append('g')
      .classed(TOPIC_PROGRESS_CLASSES.row, true)
      .attr('transform', (d, index) => `translate(0, ${(barYPosition(index))})`)
    ;

    // Gradients:
    svg
      .append('defs')
      .append('linearGradient')
      .call((gradient) => {
        d3Helpers.prepareGradient(
          gradient,
          TOPIC_PROGRESS_GRADIENT_ID,
          'horizontal',
          TOPIC_PROGRESS_COLORS.green_gradient_light,
          TOPIC_PROGRESS_COLORS.green_gradient_base,
        );
      });

    svg
      .append('defs')
      .append('linearGradient')
      .call((gradient) => {
        d3Helpers.prepareGradient(
          gradient,
          TOPIC_PROGRESS_GRADIENT_ID_REVERSE,
          'horizontal',
          TOPIC_PROGRESS_COLORS.green_gradient_base,
          TOPIC_PROGRESS_COLORS.green_gradient_light,
        );
      });

    svg
      .append('defs')
      .append('linearGradient')
      .call((gradient) => {
        d3Helpers.prepareGradient(
          gradient,
          TOPIC_PROGRESS_GRADIENT_FILTERED_ID,
          'horizontal',
          TOPIC_PROGRESS_COLORS.blue_gradient_light,
          TOPIC_PROGRESS_COLORS.blue_gradient_base,
        );
      });

    svg
      .append('defs')
      .append('linearGradient')
      .call((gradient) => {
        d3Helpers.prepareGradient(
          gradient,
          TOPIC_PROGRESS_GRADIENT_FILTERED_ID_REVERSE,
          'horizontal',
          TOPIC_PROGRESS_COLORS.blue_gradient_base,
          TOPIC_PROGRESS_COLORS.blue_gradient_light,
        );
      });

    function handleClickOnRow(index: number, element: Element): void {
      if (showChildren.includes(index)) {
        setShowChildren([]);
      } else {
        setShowChildren([ index ]);
      }
      const gridRec = document.querySelector(`.${TOPIC_PROGRESS_CLASSES.grid}`)?.getBoundingClientRect();
      const parentBounds = containerRef.current?.getBoundingClientRect();
      const triggerBounds = element.getBoundingClientRect();

      /*
        Normally would pass the element and call `getBoundingClientRect` when used, but this was returning a DOMRect with every value as zero.
        Calling it here and adjusting the offset using `scrollY` is a workaround.
      */

      setScatterPosition({
        width: gridRec?.width ?? 500,
        trigger: {
          ...triggerBounds,
          left: triggerBounds.left - parentBounds.left,
          height: triggerBounds.height,
          width: triggerBounds.width,
          top:  triggerBounds.top - parentBounds.top,
        },
      });
    }

    function printArrow(group: Selection<SVGGElement, ChartDataProps, SVGSVGElement, undefined>, isHighlighted = false): void {
      const prefersReduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const disabledAnimations = prefersReduceMotion || (isHighlighted ? disabledHighlightedAnimations : disabledMainAnimations);
      const highlightAndCompare = isHighlighted && showOthersData;
      // 1. Row - arrow:
      const arrow = group
        .append('g')
        .style('opacity', 1);

      // Gray:
      if (!highlightAndCompare) {
        arrow
          .append('rect')
          .attr('x', labelWidth)
          .attr('height', computedBarHeight)
          .attr('width', (d) => {
            let start;

            if (isHighlighted) {
              if (null == d.filtered) {
                return null;
              }

              start = xScale(d.filtered?.start as number);
            } else {
              start = xScale(d.start);
            }

            return start < 0 ? 0 : start - labelWidth;
          })
          .style('opacity', ((isHighlighted && !showOthersData) || (!isHighlighted && highlightedIds && highlightedIds?.length === 0)) ? 1 : 0.5)
          .attr('stroke-width', TOPIC_PROGRESS_STROKE_WIDTH)
          .style('stroke', TOPIC_PROGRESS_COLORS.bar_border)
          .style('fill', TOPIC_PROGRESS_COLORS.bar_light);
      }

      if (!isPdf) {
        group
          .on('mouseover click', () => {
            d3Helpers.toggleTooltip(tooltip);
          })
          .on('mouseleave', () => {
            d3Helpers.toggleTooltip(tooltip, false);
          })
          .on('mousemove', (event, row) => {
            const extraHeight = 70;

            // Prevents generating a tooltip if the highlighted element has not specific topic data
            if (row.filtered) {
              const totalStudents = row.children.elements.reduce((acc, student) => (
                highlightedIds.includes(student.id) ? acc + 1 : acc
              ), 0);

              d3Helpers.topicProgressTooltip(tooltip, totalStudents, row.filtered?.start, row.filtered?.end, type, translate, showSensitiveData, highlightedIds.length > 0);
            } else {
              d3Helpers.topicProgressTooltip(tooltip, row.children.elements.length, row.start, row.end, type, translate, showSensitiveData);
            }

            d3Helpers.moveTooltip(tooltip, width, event.layerX, event.layerY - extraHeight);
          });
      }
      // 2. Row - start > circle & line:
      const circleLine = group
        .append('g');

      circleLine
        .append('circle')
        .attr('r', (d) => {
          if (isHighlighted && null == d.filtered) {
            return null;
          }

          return TOPIC_PROGRESS_CIRCLE_RADIUS;
        })
        .attr('cx', (d) => {
          let start;

          if (isHighlighted) {
            if (null == d.filtered) {
              return null;
            }

            start = xScale(d.filtered.start);
          } else {
            start = xScale(d.start);
          }

          return start - 1;
        })
        .attr('cy', computedBarHeight / 2)
        .style('fill', !isHighlighted ? TOPIC_PROGRESS_COLORS.red : TOPIC_PROGRESS_COLORS.blue);

      circleLine
        .append('rect')
        .attr('width', (d) => {
          if (isHighlighted && null == d.filtered) {
            return null;
          }

          return TOPIC_PROGRESS_CIRCLE_RADIUS / 2;
        })
        .attr('height', computedBarHeight)
        .attr('x', (d) => {
          let start;

          if (isHighlighted) {
            if (null == d.filtered) {
              return null;
            }

            start = xScale(d.filtered?.start as number);
          } else {
            start = xScale(d.start);
          }

          return start - TOPIC_PROGRESS_CIRCLE_RADIUS / 2;
        })
        .style('fill', !isHighlighted ? TOPIC_PROGRESS_COLORS.red : TOPIC_PROGRESS_COLORS.blue)
        .attr('stroke-width', TOPIC_PROGRESS_STROKE_WIDTH)
        .style('stroke', !isHighlighted ? TOPIC_PROGRESS_COLORS.red : TOPIC_PROGRESS_COLORS.blue);

      // Color:
      arrow
        .append('path')
        .style('opacity',
          disabledAnimations
            ? ((isHighlighted && !showOthersData) || (!isHighlighted && highlightedIds && highlightedIds?.length === 0)) ? 1 : 0.9
            : 0,
        )
        .attr('d', (d) => {
          if (isHighlighted && !d.filtered) return null;

          const start = isHighlighted ? d.filtered!.start : d.start;
          const end = isHighlighted ? d.filtered!.end : d.end;

          if (end === start) return null;

          // If animation is active, the arrow is different at initial rendering to animate movement later
          const startArrow = xScale(start);
          const endArrow = disabledAnimations ? xScale(end) : xScale(start);

          return d3Helpers.barArrow(computedBarHeight, startArrow, endArrow);
        })
        .attr('stroke-width', () => highlightAndCompare ? TOPIC_PROGRESS_FILTERED_ARROW_STROKE : TOPIC_PROGRESS_STROKE_WIDTH)
        .style('stroke', TOPIC_PROGRESS_COLORS.bar_border)
        .style('fill', (d) => {
          if ((isHighlighted && !d.filtered) || highlightAndCompare) return 'none';

          const start = isHighlighted ? d.filtered!.start : d.start;
          const end = isHighlighted ? d.filtered!.end : d.end;

          return end > start
            ? `url(#${!isHighlighted ? TOPIC_PROGRESS_GRADIENT_ID : TOPIC_PROGRESS_GRADIENT_FILTERED_ID})`
            : `url(#${!isHighlighted ? TOPIC_PROGRESS_GRADIENT_ID_REVERSE : TOPIC_PROGRESS_GRADIENT_FILTERED_ID_REVERSE})`
          ;
        })
        .call((path) => {
          if (disabledAnimations) {
            return;
          }

          let stagger = 0;
          path
            .transition()
            .delay((d) => d.start !== d.end ? 100 + (100 * stagger++) : 0)
            .duration(TOPIC_PROGRESS_CHARTS_ANIMATION_DURATION)
            .ease(d3.easeBackInOut)
            .style('opacity', ((isHighlighted && !showOthersData) || (!isHighlighted && highlightedIds && highlightedIds?.length === 0)) ? 1 : 0.9)
            .attr('d', (d) => {
              if (isHighlighted && !d.filtered) return null;

              const start = isHighlighted ? d.filtered!.start : d.start;
              const end = isHighlighted ? d.filtered!.end : d.end;

              if (end === start) return null;

              // If animation is active, the arrow is different at initial rendering to animate movement later
              const startArrow = xScale(start);
              const endArrow = xScale(end);

              return d3Helpers.barArrow(computedBarHeight, startArrow, endArrow);
            });
        });
    }

    // Handles 0 elements selected + hide others = true
    if ((!showOthersData && !highlightedIds?.length) || 0 === formattedData.length) {
      if (0 === formattedData.length) {
        const emptyHeight = 80;

        svg
          .attr('height', emptyHeight)
          .attr('viewBox', `0 0 ${width} ${emptyHeight}`)
        ;
      }

      svg
        .append('text')
        .text(`${translate('global.CHARTS.NO_DATA')}`)
        .attr('x', labelWidth + (axisWidth / 2))
        .attr('y', '50%')
        .attr('alignment-baseline', 'middle')
        .attr('text-anchor', 'middle')
        .style('font-style', 'italic');
    }

    // Base row elements - hidden when 'hide other' is true:
    if (showOthersData) {
      rowData
        .append('g')
        .call((group) => printArrow(group));
    }

    // Highlighted elements - shown when ids are filtered:
    if (highlightedIds && highlightedIds?.length > 0) {
      rowData
        .append('g')
        .call((group) => printArrow(group, true));
    }

    // Label container:
    const label = rowData
      .append('g')
      .attr('transform', `translate(${labelWidth - labelOffset},0)`)
    ;

    // Label text handler:
    const text = label
      .append('text')
      .attr('y', computedBarHeight / 1.5)
    ;

    // Label text:
    text
      .append('tspan')
      .classed(TOPIC_PROGRESS_CLASSES.label, true)
      .attr('x', 0)
      .attr('text-anchor', 'end')
      .style('font-size', TOPIC_PROGRESS_LABEL_SIZE)
      .text((d) => {
        const text = d.topic.name;

        if (d3Helpers.getTextWidth(text, TOPIC_PROGRESS_LABEL_SIZE) > labelWidth - 100) {
          return d3Helpers.sliceText(d.topic.name);
        }

        return d.topic.name;
      })
      .attr('title', (d) => d.topic.name)
    ;

    if (showSensitiveData) {
      label
        .append('g')
        .style('pointer-events', 'bounding-box')
        .classed(TOPIC_PROGRESS_CLASSES.chevron, true)
        .attr('transform', 'translate(25, 5)')
        .each(function (this, data) {
          d3
            .select(this)
            .on('click', (event) => handleClickOnRow(data.topic.index, event.currentTarget))
            .call((group) => {
              if (showChildren.includes(data.topic.index)) {
                // is open
                group
                  .node()
                  ?.appendChild(fetchedIcons.collapseIcons.collapseOn.cloneNode(true));
              } else {
                // is close
                group
                  .node()
                  ?.appendChild(fetchedIcons.collapseIcons.collapseOff.cloneNode(true));
              }
            });
        })
      ;
    }

    // Grid
    const grid = d3
      .axisBottom(xScale)
      .ticks(10)
      .tickSize(height)
      .tickFormat(() => '');

    svg
      .append('g')
      .attr('transform', `translate(0,${(showSensitiveData ? TOPIC_PROGRESS_TITLE_OFFSET : 0) + (TOPIC_PROGRESS_BAR_HEIGHT / 2)})`)
      .attr('class', TOPIC_PROGRESS_CLASSES.grid)
      .call(grid);

    // Appends content into the dom:
    container.node()?.appendChild(svg.node() as Node);

    if (isPdf && pdfIds && highlightedIds.length) {
      if (pdfIds.indexOf(highlightedIds[0]) === pdfIds.length - 1) {
        setIsPdfReady?.(true);
      }
    }
  };

  useEffect(() => {
    if (containerRef.current) {
      setWidth(containerRef.current.offsetWidth);
    }
  }, [ containerRef, windowSize ]);

  useEffect(() => {
    if (highlightedIds?.length === 1) {
      setShowChildren([]);
    }
  }, [ highlightedIds ]);

  useEffect(() => {
    if (topics.length > 0 && width > 0 && icons) {
      draw(
        topics,
        {
          width: width,
          margin: {
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
          },
          padding: 5,
        },
        isPdf || ((showChildren !== prevShowChildren || showOthersData !== prevshowOthersData
          || highlightedIds !== prevHighlightedIds) && !play),
        isPdf || ((highlightedIds === prevPrevHighlightedIds) && !play),
      );
    }
  }, [ topics, showOthersData, highlightedIds, width, icons, showChildren, showSensitiveData, play ]);

  const topic = showChildren?.length ? (topics.find((topic) => topic.index === showChildren[0])) : null;
  const students = topic ? topic?.elements.map((student) => ({
    id: student.id,
    fullName: student.fullName,
    gender: student.gender,
    color: student.color,
    lastAssessmentDate: DateTime.now(),
    chartsData: {
      mathsAge: student.endTopicAge,
      mathsAgeImprovement: student.progressTopicAge,
    },
  })) as ChartDataType[] : [];

  const noData = topics.length === 0;

  return (
    <>
      <GlobalStyle />
      <div ref={containerRef}>
        {!isPdf && (
          noData
            ? <Alert show className={'mt-md'}>
              <div>
                <p>{translate(`pages.REPORTS.SECTIONS.TOPIC_PROGRESS_TABLE.NO_STUDENTS.line1_${type === 'student' ? 'class' : 'school'}`)}</p>
                <p>{translate('pages.REPORTS.SECTIONS.TOPIC_PROGRESS_TABLE.NO_STUDENTS.line2')}</p>
              </div>
            </Alert>
            : <Loader />
        )}
      </div>
      <ScatterLineChart
        visible={!!students?.length}
        title={topic?.name}
        onClose={() => setShowChildren([])}
        position={scatterPosition}
      >
        <StudentProgress
          thinAxis
          showOthersData={showOthersData}
          data={students}
          chartType={'mathsAge'}
          highlightedIds={highlightedIds}
          removeUnassessed={true}
          type={type}
          showProgress
        />
      </ScatterLineChart>
    </>
  );
};

const ScatterLineChart = ({
  children,
  visible,
  icon,
  title,
  onClose,
  position,
}: ScatterLineChartProps) => {
  const { translate } = useI18n();
  const { trigger } = position ?? { };
  if (!(trigger && visible)) {
    return null;
  } else {
    const padding = 30;
    const left = trigger.left;
    const top = trigger.top + trigger.height + padding;

    return (
      <div className='absolute top-0 left-0'>
        <div
          className={'absolute rounded-default border text-center bg-white shadow-lg z-10'}
          style={{
            top: top,
            left: left,
            width: position?.width,
          }}
        >
          <div className={'container flex items-center pb-md border-b-2 border-header'}>
            <div className='flex items-center'>
              {icon &&
                <span className={'mr-sm'}>
                  {getIconComponent(icon, 'md', 'dark')}
                </span>
              }
              {title && (
                <h5 className={'font-bold'}>{title}</h5>
              )}
            </div>
            <div className={'ml-auto'}>
              <ButtonIcon
                icon={GLOBAL_ICONS.close}
                onClick={onClose}
                size={'sm'}
                color={'light'}
                isOutline
                isCircle
              />
            </div>
          </div>
          <div className={'flex w-full justify-center mt-2'}>
            {translate('pages.REPORTS.SECTIONS.TOPIC_PROGRESS.topic_ages')}
          </div>
          {children}
        </div>
      </div>
    );
  }
};

TopicProgress.displayName = 'TopicProgress';

export default TopicProgress;
